import { useCallback, useEffect, useState } from "react";
import { http, formatError } from "@/lib/api";
import { logWarn } from "@/lib/log";
import { Card, ErrorBox, Skeleton, Stat } from "@/components/staff/ui";

const usd = (n) => `$${(n || 0).toFixed(2)}`;

const TestModeNote = () => (
  <div className="text-xs text-neutral-500 space-y-1" data-testid="rev-test-note">
    <p>These numbers come from test-mode payments until your Stripe account is claimed and live.</p>
    <p>To go live: open Manage → Payments, follow the steps to claim your Stripe account and finish verification, then republish the app.</p>
  </div>
);

function DailyChart({ d }) {
  const max = Math.max(1, ...d.daily.map((x) => x.amount));
  return (
    <Card title="Daily payments recorded (30 days)" testid="rev-chart"
      action={<span className="text-xs text-neutral-500">Subscriptions {usd(d.split_30d.subscriptions)} · Passes {usd(d.split_30d.day_passes)}</span>}>
      <div className="flex items-end gap-1 h-36">
        {d.daily.map((x) => <div key={x.date} title={`${x.date}: ${usd(x.amount)}`} className="flex-1 bg-sky-400/70 hover:bg-sky-400 rounded-t transition-colors" style={{ height: `${Math.max(2, (100 * x.amount) / max)}%` }} />)}
      </div>
    </Card>
  );
}

const Lines = ({ title, rows, testid, empty }) => (
  <Card title={title} testid={testid}>
    {!rows.length && <p className="text-sm text-neutral-500">{empty}</p>}
    {rows.map(([k, v]) => <p key={k} className="text-sm flex justify-between gap-3 py-1 capitalize"><span>{k}</span><span className="text-neutral-400 tabular-nums">{v}</span></p>)}
  </Card>
);

// The former Revenue page: payments BrynAI recorded from Stripe checkouts and renewals.
export function RevenueSummary() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    setErr("");
    http.get("/admin/revenue").then((r) => setD(r.data)).catch((e) => { logWarn("Revenue load failed", e); setErr(formatError(e)); });
  }, []);
  useEffect(() => { load(); }, [load]);
  if (err) return <ErrorBox error={`Couldn't load revenue right now: ${err}`} onRetry={load} testid="rev-error" />;
  if (!d) return <Skeleton rows={4} />;
  return (
    <div className="space-y-4" data-testid="rev-summary">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-display text-lg font-semibold text-white">Subscriptions and revenue <span className="text-xs font-normal text-neutral-500" data-testid="rev-mode-badge">· {d.mode === "live" ? "Live" : "Test"} mode</span></h2>
        {d.mode !== "live" && <TestModeNote />}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Monthly recurring revenue (est.)" value={usd(d.mrr)} testid="rev-mrr" />
        <Stat label="Active subscribers" value={d.subscribers} testid="rev-subscribers" />
        <Stat label="Payments recorded, last 30 days" value={usd(d.revenue_30d)} testid="rev-30d" />
        <Stat label="New / canceled this month" value={`${d.new_this_month} / ${d.canceled_this_month}`} testid="rev-new-canceled" />
      </div>
      <DailyChart d={d} />
      <div className="grid lg:grid-cols-3 gap-4">
        <Lines title="Subscribers by plan" testid="rev-tiers" empty="No subscribers yet." rows={Object.entries(d.tiers).map(([t, v]) => [t, `${v.monthly} monthly · ${v.annual} annual`])} />
        <Lines title="Passes sold (30 days)" testid="rev-passes" empty="No passes sold." rows={Object.entries(d.day_passes_30d).map(([k, v]) => [k.replace("_day", ""), v])} />
        <Lines title={`Discount codes · ${usd(d.discount_given_total)} given in total`} testid="rev-codes" empty="No codes yet."
          rows={d.codes.map((c) => [c.code, `${c.percent_off}% · ${c.uses ?? "–"} uses`])} />
      </div>
    </div>
  );
}
